import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "../context/auth-context";
import { Building2, Loader2, AlertCircle, Shield } from "lucide-react";
import { useUserCompanies } from "@/hooks/use-user-companies";
import { markCompanySelected } from "@/lib/api";
import { AuthLayout } from "./auth-layout";

export function SelectCompanyForm() {
  const navigate = useNavigate();
  const {
    selectedSubscriptionId,
    selectedCompanyId,
    setSelectedCompanyId,
    setSelectedSubscriptionId,
  } = useAuth();
  const {
    companies,
    isLoading: loading,
    isError,
  } = useUserCompanies(selectedSubscriptionId ?? undefined);
  const [selected, setSelected] = useState<string | null>(null);

  const error = isError ? "No fue posible cargar empresas" : null;

  useEffect(() => {
    if (selectedCompanyId) {
      navigate("/", { replace: true });
    }
  }, [selectedCompanyId, navigate]);

  useEffect(() => {
    if (!selectedCompanyId) {
      try {
        const storedCompanyId = localStorage.getItem("selectedCompanyId");
        if (storedCompanyId) {
          setSelectedCompanyId(storedCompanyId);
        }
      } catch (restoreError) {
        console.error(
          "Error restaurando empresa desde localStorage:",
          restoreError,
        );
      }
    }
  }, [selectedCompanyId, setSelectedCompanyId]);

  useEffect(() => {
    if (!selectedSubscriptionId) {
      navigate("/select-subscription", { replace: true });
    }
  }, [selectedSubscriptionId, navigate]);

  const canConfirm = Boolean(selected);

  const handleConfirm = () => {
    if (!selected) return;
    setSelectedCompanyId(selected);
    markCompanySelected();
    navigate("/", { replace: true });
  };

  const handleGoBack = () => {
    setSelectedSubscriptionId(null);
    navigate("/select-subscription", { replace: true });
  };

  return (
    <AuthLayout>
      <form onSubmit={(e) => e.preventDefault()}>
        <FieldGroup>
          <div className="flex flex-col items-center gap-2 text-center">
            <h1 className="text-2xl font-bold">Selecciona una empresa</h1>
            <p className="text-sm text-muted-foreground">
              Elige la empresa con la que deseas trabajar en esta sesión
            </p>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="mb-3 h-8 w-8 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">
                Cargando empresas...
              </span>
            </div>
          ) : null}

          {error ? (
            <div className="rounded-md bg-destructive/10 p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            </div>
          ) : null}

          {!loading && !error && companies.length === 0 ? (
            <div className="py-8 text-center">
              <Building2 className="mx-auto mb-3 h-12 w-12 text-muted-foreground opacity-50" />
              <p className="mb-1 text-sm font-medium text-muted-foreground">
                No tienes acceso a ninguna empresa en esta suscripción
              </p>
              <p className="text-xs text-muted-foreground">
                Contacta al administrador para obtener acceso
              </p>
            </div>
          ) : null}

          {!loading && !error && companies.length > 0 ? (
            <Field>
              <FieldLabel htmlFor="company-select">Empresa</FieldLabel>
              <Select
                value={selected ?? ""}
                onValueChange={(value) => setSelected(value || null)}
              >
                <SelectTrigger id="company-select" className="w-full">
                  <SelectValue placeholder="Selecciona una empresa..." />
                </SelectTrigger>
                <SelectContent>
                  {companies.some((c) => c.id === "TENANT") ? (
                    <>
                      <div className="relative py-2">
                        <div className="absolute inset-0 flex items-center px-2">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">
                            Tenant
                          </span>
                        </div>
                      </div>
                      {companies
                        .filter((c) => c.id === "TENANT")
                        .map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            <div className="flex items-center gap-2">
                              <Shield className="h-4 w-4 shrink-0 text-amber-500" />
                              <span>{company.name}</span>
                              <span className="ml-auto text-xs text-muted-foreground">
                                Administrar datos del tenant
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                    </>
                  ) : null}
                  {companies.some((c) => c.id !== "TENANT") ? (
                    <>
                      <div className="relative py-2">
                        <div className="absolute inset-0 flex items-center px-2">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">
                            Compañías
                          </span>
                        </div>
                      </div>
                      {companies
                        .filter((c) => c.id !== "TENANT")
                        .map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            <div className="flex items-center gap-2">
                              <span
                                className={`h-2.5 w-2.5 shrink-0 animate-pulse rounded-full ${
                                  company.active !== false
                                    ? "bg-green-500"
                                    : "bg-red-500"
                                }`}
                              />
                              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                              <span>{company.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                    </>
                  ) : null}
                </SelectContent>
              </Select>
            </Field>
          ) : null}

          <Field>
            {!loading && !error && companies.length > 0 ? (
              <Button
                type="button"
                onClick={handleConfirm}
                disabled={!canConfirm}
                className="w-full"
              >
                Continuar
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoBack}
              className="w-full"
            >
              Volver atrás
            </Button>
          </Field>
        </FieldGroup>
      </form>
    </AuthLayout>
  );
}
